import React, { useEffect, useState } from "react";
import type { BrandKit as Kit } from "@veyso/core";
import { Field } from "../components/ui.js";
import { api } from "../api.js";
import { capture } from "../lib/posthog.js";

/**
 * The site the brand was read from, and the way to read it again.
 *
 * Onboarding asks for this once and then it was invisible for ever — which is
 * wrong twice over. A product moves to its own domain, or rebrands, and the kit
 * that was read a year ago is the only thing still describing the old one; and
 * somebody who skipped onboarding had no way to reach the reader at all.
 *
 * It applies what it reads to the form rather than saving it. The page already
 * has a Save button and a dirty state, so a read that lands in the form is
 * reversible by walking away — which is the right weight for a guess. Saving on
 * their behalf would make a stray keystroke overwrite a brand somebody had
 * tuned by hand.
 */
export const BrandWebsite: React.FC<{ kit: Kit; onChange: (kit: Kit) => void }> = ({ kit, onChange }) => {
  const [website, setWebsite] = useState(kit.website);
  const [reading, setReading] = useState(false);

  /**
   * The settings arrive after the first render, so the initial state was
   * captured from an empty default kit and the field stayed blank with the
   * saved address sitting right above it in the preview.
   *
   * Keyed on the saved value rather than on every render, so a half-typed
   * address is not wiped by an unrelated change to the kit.
   */
  useEffect(() => {
    setWebsite(kit.website);
  }, [kit.website]);

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const read = async () => {
    const asked = website.trim();
    if (!asked || reading) return;

    setReading(true);
    setError(null);
    setNote(null);
    try {
      const guess = await api.brandFromWebsite(asked);
      onChange({
        ...kit,
        website: guess.website,
        colors: guess.colors,
        // Only what was read. Overwriting a face with null would replace a real
        // choice with nothing.
        titleFont: guess.titleFont ?? kit.titleFont,
        bodyFont: guess.bodyFont ?? kit.bodyFont,
        // Only when one was actually found. A site with no usable mark must
        // not clear a logo somebody uploaded precisely because of that.
        logoKey: guess.logoKey ?? kit.logoKey,
      });
      setWebsite(guess.website);

      /**
       * What the site is actually set in, said out loud.
       *
       * Most sites use a face we do not ship — Geist, NotionInter, a licensed
       * cut — and substituting the nearest one silently is how somebody ends up
       * wondering why their videos are not in their font. Naming it lets them
       * decide whether the substitute will do.
       */
      const typeface = guess.typeface;
      const parts = ["Read."];

      if (typeface && !typeface.rendered) {
        parts.push(
          `That site is set in ${typeface.name}, which we cannot render — the nearest we ship is ${guess.titleFont ?? "Inter"}.`,
        );
      }

      /**
       * Whether a mark came back, either way.
       *
       * Silence after a successful read means "we got everything", and for the
       * logo that is often untrue — the scrape finds nothing usable on plenty
       * of sites. Saying so here is the difference between uploading one now
       * and discovering the omission in a finished video.
       */
      if (guess.logoKey) {
        parts.push("A logo was found and will be used.");
      } else if (!kit.logoKey) {
        parts.push("No logo could be pulled from that site — upload one below, or the product name is set as type.");
      } else {
        parts.push("No logo on that site; the one you uploaded is kept.");
      }

      parts.push("Nothing is saved until you press Save.");
      setNote(parts.join(" "));
      capture("brand_reread_from_settings");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setReading(false);
    }
  };

  return (
    <section className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
      <h2 className="text-[13px] font-semibold text-ink-100">{"Product website"}</h2>
      <p className="mt-1 text-[12px] leading-[1.5] text-ink-300">
        {"Where this kit was read from. Change it and read again if the product has moved or been redesigned."}
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[16rem] flex-1">
          <Field label="Address">
            <input
              className="input w-full"
              placeholder="acme.com"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void read();
                }
              }}
              autoComplete="url"
              spellCheck={false}
            />
          </Field>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => void read()}
          disabled={reading || website.trim().length === 0}
        >
          {reading ? "Reading…" : "Read brand from this site"}
        </button>
      </div>

      {note ? <p className="mt-2.5 text-[12px] text-ink-300">{note}</p> : null}
      {error ? <p className="mt-2.5 text-[12px] text-signal-bad">{error}</p> : null}
    </section>
  );
};
